import { http, type RequestOptions } from './http';
import { ROUTES } from './config';
import type { CmsLayoutEntry, CmsSection, Customer, Customer360, CustomerPreferences } from '../types/domain';

/** CrmService client (customers, preferences, CMS, customer-360 aggregation). */
export const crmApi = {
  listCustomers(): Promise<Customer[]> {
    return http.get(ROUTES.customers);
  },

  getCustomer(id: string): Promise<Customer> {
    return http.get(`${ROUTES.customers}/${id}`);
  },

  upsertCustomer(
    body: Partial<Customer> & { customerId?: string | null },
    options?: RequestOptions
  ): Promise<{ customerId: string }> {
    return http.post(ROUTES.customers, body, options);
  },

  /**
   * The signed-in user's own CRM customer record — resolves/auto-provisions it from the
   * JWT (by UserId, then by verified email, then creates one). Call this once after login
   * to get the customerId "My Bookings" and the profile page are keyed on.
   *
   * `options` matters most for server-side callers (see RequestOptions.accessToken) —
   * there's no tokenStore/localStorage in a Next.js server action.
   */
  async getMe(options?: RequestOptions): Promise<Customer & { provisioned: boolean }> {
    const res = await http.get<{
      customerId: string; userId?: string; name: string; email: string; phone: string;
      addressLine: string; suburb: string; city: string;
      preferredContactMethod: string; preferredTime: string;
      rowVersion: number; provisioned: boolean;
    }>(`${ROUTES.customers}/me`, options);
    return {
      id: res.customerId,
      userId: res.userId,
      name: res.name,
      email: res.email,
      phone: res.phone,
      addressLine: res.addressLine,
      suburb: res.suburb,
      city: res.city,
      preferredContactMethod: res.preferredContactMethod,
      preferredTime: res.preferredTime,
      rowVersion: res.rowVersion,
      provisioned: res.provisioned,
    };
  },

  updatePreferences(customerId: string, prefs: CustomerPreferences): Promise<void> {
    return http.put(`${ROUTES.customers}/${customerId}/preferences`, prefs);
  },

  /** Customer-360: CRM fans out to Booking/Scheduling/Billing (Pattern 1). */
  getCustomer360(customerId: string): Promise<Customer360> {
    return http.get(`${ROUTES.customers}/${customerId}/dashboard`);
  },

  removeCustomer(id: string): Promise<void> {
    return http.delete(`${ROUTES.customers}/${id}`);
  },

  // ── CMS ──
  getPublicPage(page: string): Promise<CmsSection[]> {
    return http.get(ROUTES.cms.public(page), { anonymous: true });
  },

  /** Anonymous — block visibility/order for a page (see CmsLayoutEntry). */
  getPublicLayout(page: string): Promise<CmsLayoutEntry[]> {
    return http.get(`${ROUTES.cms.public(page)}/layout`, { anonymous: true });
  },

  /** Manager — create the default block rows a page is missing so every storefront block is listed (idempotent). */
  ensureBlocks(page: string): Promise<{ created: string[] }> {
    return http.post(`/api/v1/cms/blocks/ensure?page=${encodeURIComponent(page)}`, undefined);
  },

  listSections(page?: string): Promise<CmsSection[]> {
    const q = page ? `?page=${encodeURIComponent(page)}` : '';
    return http.get(`${ROUTES.cms.sections}${q}`);
  },

  createSection(body: CmsSection): Promise<{ id: string }> {
    return http.post(ROUTES.cms.sections, body);
  },

  updateSection(id: string, body: CmsSection): Promise<void> {
    return http.put(`${ROUTES.cms.sections}/${id}`, body);
  },

  removeSection(id: string): Promise<void> {
    return http.delete(`${ROUTES.cms.sections}/${id}`);
  },
};
